"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { getAllEmployees } from "api/apiHelper"
import { getClausesAPI } from "api/apiHelper"
import { createContractAPI } from "api/apiHelper"
import ContractClausePicker from "./ContractClausePicker"
import { toast } from "react-toastify"
import ErrorBox from "components/ErrorBox"
import type { AxiosResponse } from "axios"
import { useNavigate } from "react-router-dom"
import type { ContractType } from "pages/contracts/ShowContract"
import Backdrop from "components/Backdrop"
import { usePermissions } from "app/hooks"
// import type { EmployeeType } from "types/EmployeeType"

export interface ClauseType {
  id: number
  clause_text: string
  legal_text: string
}

const CreateContract: React.FC = () => {
  const [selectEmployees, setSelectEmployees] = useState<EmployeeType[]>([])
  const [selectClauses, setSelectClauses] = useState<ClauseType[]>([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [cnic, setCNIC] = useState<string>("")
  const [selectedClauses, setSelectedClauses] = useState<ClauseType[]>([])
  const [allClauses, setAllClauses] = useState<ClauseType[]>([])
  const [error, setError] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const permissions = usePermissions() || []
  const navigate = useNavigate()

  const handleCancel = () => {
    setSelectedEmployeeId("")
    setFirstName("")
    setLastName("")
    setCNIC("")
    setSelectedClauses([])
    setError("")
  }

  const getEmployees = async () => {
    if (permissions && permissions.includes("employees.view_employee")) {
      try {
        const response: AxiosResponse<EmployeeType[]> = await getAllEmployees()
        const employees: EmployeeType[] = response.data
        setSelectEmployees(employees)
      } catch (error: any) {
        console.log(error)
        setError(error.message)
      }
    }
  }

  const fetchAllClauses = async () => {
    if (permissions && permissions.includes("contracts.view_contractclause")) {
      try {
        const response: AxiosResponse<ClauseType[]> = await getClausesAPI()
        const clauses: ClauseType[] = response.data
        setAllClauses(clauses)
        setIsLoading(false)
      } catch (error: any) {
        console.log(error)
        setError(error.message)
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getEmployees()
    fetchAllClauses()
  }, [])

  const handleEmployeeChange = (selectedId: string) => {
    setSelectedEmployeeId(selectedId)
    const selectedEmployee = selectEmployees.find((employee) => employee.id.toString() === selectedId)
    if (selectedEmployee) {
      setFirstName(selectedEmployee.first_name)
      setLastName(selectedEmployee.last_name)
      setCNIC(selectedEmployee.CNIC)
      setSelectClauses(allClauses)
      setSelectedClauses([])
      setError("")
    } else {
      console.error("Invalid employee ID selected")
      setError("Invalid employee selected")
      setSelectedEmployeeId("")
      setFirstName("")
      setLastName("")
      setCNIC("")
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    const notification = toast.loading("Creating Contract...")

    const contractData = {
      employee: selectedEmployeeId,
      clauses_in_contract: selectedClauses.map((clause) => clause.id),
    }

    try {
      const response: AxiosResponse<ContractType> = await createContractAPI(contractData)
      console.log("Contract created successfully:", response.data)
      toast.update(notification, {
        render: "Contract created successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      })
      navigate(response.data?.id ? `/employee/contract/${response.data.id}` : "/")
    } catch (error) {
      console.error("Error creating contract:", error)
      toast.update(notification, {
        render: "Failed to create contract",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClauseClick = (clauseId: number, isFromAvailable: boolean) => {
    if (isFromAvailable) {
      const selectedClause = selectClauses.find((clause) => clause.id === clauseId)
      if (selectedClause) {
        setSelectedClauses([...selectedClauses, selectedClause])
        setSelectClauses(selectClauses.filter((clause) => clause.id !== clauseId))
      }
    } else {
      const selectedClause = selectedClauses.find((clause) => clause.id === clauseId)
      if (selectedClause) {
        setSelectClauses([...selectClauses, selectedClause])
        setSelectedClauses(selectedClauses.filter((clause) => clause.id !== clauseId))
      }
    }
  }

  if (isLoading) {
    return <Backdrop show={isLoading} />
  }

  if (!permissions.includes("employees.view_employee") || !permissions.includes("contracts.view_contractclause")) {
    return (
      <div className="container mt-5">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h3>
            <p className="text-gray-600 dark:text-gray-300">
              You don't have sufficient privileges to create contracts.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
      <div>
        {error && (
          <div className="mb-6">
            <ErrorBox errorMessage={error} />
          </div>
        )}

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Employee Selection Section */}
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Employee Selection</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Choose the employee for this contract</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="select_employee"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Select Employee *
                    </label>
                    <select
                      id="select_employee"
                      name="select_employee"
                      value={selectedEmployeeId}
                      onChange={(event) => handleEmployeeChange(event.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      required
                    >
                      <option value="">Select an employee</option>
                      {selectEmployees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedEmployeeId && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={firstName}
                          disabled
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={lastName}
                          disabled
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">CNIC</label>
                        <input
                          type="text"
                          value={cnic}
                          disabled
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contract Clauses Section */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-8">
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Contract Clauses</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedEmployeeId
                      ? "Select the clauses to include in this contract"
                      : "Select an employee first to choose contract clauses"}
                  </p>
                </div>

                {selectedEmployeeId ? (
                  <ContractClausePicker
                    selectClauses={selectClauses}
                    selectedClauses={selectedClauses}
                    onClauseClick={handleClauseClick}
                  />
                ) : (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-user-plus text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      Select an employee to choose contract clauses
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedEmployeeId || selectedClauses.length === 0 || isSubmitting}
                  className="px-6 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 transition-colors flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Create Contract
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateContract
